/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Box,
    Center,
    Flex,
    Spinner,
    Stack,

    // Hooks
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {ListMenuPopover} from './list-menu-popover'
import Link from '@salesforce/retail-react-app/app/components/link'

/* Context */
import {useGlobalNavigation} from '../../contexts/global-elements-context'
import {useHeaderContext} from '../../contexts/header-context'

/* Utils */
import {mergeCMSCategoryReplacements} from '../../components/optimizely/utils/utils'

// Others
import {categoryUrlBuilder} from '../../utils/url'

// Constants
const MAXIMUM_NUMBER_COLUMNS = 5

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({
    root,
    contentComponent,
    categories,
    itemsKey,
    itemsCountKey,
    maxColumns = MAXIMUM_NUMBER_COLUMNS
}) => {
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const intl = useIntl()
    const {data: globalNavigationData} = useGlobalNavigation()
    const {category} = useHeaderContext()
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const items = root?.[itemsKey]

    // Determine the active top-level category ID from the current page category
    // The parentCategoryTree includes the root category at index 0, so we need index 1 for the actual navigation category
    // If parentCategoryTree has only 1 item (the root), or if we're on a top-level category itself, use category.id
    const activeTopLevelCategoryId =
        category?.parentCategoryTree?.length > 1 ? category.parentCategoryTree[1].id : category?.id

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex {...baseStyle.container}>
                {items ? (
                    <Stack direction={'row'} spacing={0} {...baseStyle.stackContainer}>
                        {items?.map?.((item) => {
                            const processedCategoryWithCMS = mergeCMSCategoryReplacements(
                                item,
                                globalNavigationData
                            )
                            const {id, name} = processedCategoryWithCMS
                            const itemsCount =
                                processedCategoryWithCMS[itemsCountKey] ||
                                processedCategoryWithCMS[itemsKey]?.length ||
                                0
                            const cmsCategoryReplacement =
                                processedCategoryWithCMS.CategoryReplacement?.ContentLink?.Expanded
                            const isActive = activeTopLevelCategoryId === id

                            return (
                                <Box key={id}>
                                    {itemsCount > 0 ? (
                                        <ListMenuPopover
                                            key={id}
                                            maxColumns={maxColumns}
                                            item={processedCategoryWithCMS}
                                            name={name}
                                            items={item?.[itemsKey]}
                                            itemsKey={itemsKey}
                                            categories={categories}
                                            contentComponent={contentComponent}
                                            isActive={isActive}
                                        />
                                    ) : (
                                        <Link
                                            as={RouteLink}
                                            to={categoryUrlBuilder(item, categories)}
                                            onMouseOver={setActiveLink.bind(this, id)}
                                            onMouseOut={setActiveLink.bind(this)}
                                            {...baseStyle.listMenuTriggerLink}
                                            {...{name: name + ' __'}}
                                            {...(activeLink === id || isActive
                                                ? baseStyle.listMenuTriggerLinkActive
                                                : {})}
                                            /* CMS Props */
                                            color={cmsCategoryReplacement?.TextColor}
                                            backgroundColor={
                                                cmsCategoryReplacement?.TextBackgroundColor
                                            }
                                        >
                                            {cmsCategoryReplacement?.OverrideName || name}
                                        </Link>
                                    )}
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Center p="2">
                        <Spinner size="lg" />
                    </Center>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Customize the component used to render the list menu item
     */
    contentComponent: PropTypes.elementType,
    /**
     * Map of categoryId -> full category data (includes c_relativeUrl) from SSR
     */
    categories: PropTypes.object
}

export {ListMenu}
